import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { CrateCategoryList, CrateCategoryListProps, LibraryTabsContext, CRATE_ALL_ITEM, Title } from '@bpm-web-app/components';
import { BreakpointView, EmptyState, GhostComponent, SecondaryPageTitle } from '@bpm-web-app/components/shared';
import { useOfflineCrate, useOfflineCrateCategories, useOnlineCrate, useOnlineCrateCategories } from '@bpm-web-app/swr-hooks';
import { DOWNLOAD_ONLY, Hub, unique } from '@bpm-web-app/utils';
import { Artist } from '@bpm-web-app/stream-api-sdk';
import classNames from 'classnames';
import { PageHeader } from '@bpm-web-app/components/src/lib/shared/page-header/page-header';
import { LocalSearchSort } from '@bpm-web-app/components/src/lib/nav-my-playlist-categories/utils';
import styles from './index.module.css';

const CrateLandingPage: FC<{ title: string }> = ({ title }: { title: string }) => {
    const { libraryProperty } = useContext(LibraryTabsContext);

    const { data: offlineData, isLoading: isLoadingOfflineData } = useOfflineCrate(libraryProperty);
    const { data: offlineCrateCategoriesData, isLoading: isLoadingOfflineCategoriesData } = useOfflineCrateCategories(libraryProperty);

    const { data: onlineData, isLoading: isLoadingOnlineData } = useOnlineCrate(libraryProperty);
    const { data: onlineCrateCategoriesData, isLoading: isLoadingOnlineCrateCategories } = useOnlineCrateCategories(libraryProperty);

    const isEverythingLoaded = useMemo(() => {
        if (!isLoadingOfflineData && !isLoadingOfflineCategoriesData && !isLoadingOnlineData && !isLoadingOnlineCrateCategories) return true;
        return false;
    }, [isLoadingOfflineData, isLoadingOfflineCategoriesData, isLoadingOnlineData, isLoadingOnlineCrateCategories]);

    const [sortType, setSortType] = useState<LocalSearchSort>(undefined);

    const isCrateAllItem = useCallback((id: string | number) => `${id}` === CRATE_ALL_ITEM.id, []);

    const mapCategoryTracksToCrateDetails = useCallback(
        (tracksForCategory: (typeof offlineData)['data'] | (typeof onlineData)['data'], categoryId: number | string) => {
            const categoryTrackArtists: Artist[] = tracksForCategory?.map((track) => track.album?.artists?.[0]).filter(Boolean);
            const categoryTrackAlbums = tracksForCategory?.map((track) => track.album);

            return {
                id: String(categoryId),
                artists: unique(categoryTrackArtists, 'id'),
                coverUrl: tracksForCategory?.[0]?.album?.cover_url || '',
                coverColor: tracksForCategory?.[0]?.album?.cover_color || '#FFFFFF',
                slug: isCrateAllItem(categoryId) ? CRATE_ALL_ITEM.slug : tracksForCategory?.[0]?.album.category.slug,
                name: isCrateAllItem(categoryId) ? CRATE_ALL_ITEM.name : tracksForCategory?.[0]?.album.category.name,
                created_at: tracksForCategory?.[0]?.added_to_crate_at,
                albums: categoryTrackAlbums,
            };
        },
        [isCrateAllItem]
    );

    const getCategoryTracks = useCallback(
        (categoryId: string | number, hub: Hub): CrateCategoryListProps['items'][0] => {
            const tracksData = hub === Hub.Download ? onlineData?.data || [] : offlineData?.data || [];

            if (isCrateAllItem(categoryId)) {
                return mapCategoryTracksToCrateDetails(tracksData, categoryId);
            }

            const tracksForCategory = (tracksData as any).filter((item) => item.album?.category.id === +categoryId);

            return mapCategoryTracksToCrateDetails(tracksForCategory, +categoryId);
        },
        [isCrateAllItem, mapCategoryTracksToCrateDetails, offlineData?.data, onlineData?.data]
    );

    const offlineCategoryList = useMemo<CrateCategoryListProps['items']>(
        () => (offlineCrateCategoriesData?.data?.length ? [CRATE_ALL_ITEM, ...offlineCrateCategoriesData.data]?.map((category) => getCategoryTracks(category.id, Hub.Stream)) : []),
        [getCategoryTracks, offlineCrateCategoriesData?.data]
    );

    const onlineCategoryList = useMemo<CrateCategoryListProps['items']>(
        () => (onlineCrateCategoriesData?.data?.length ? [CRATE_ALL_ITEM, ...onlineCrateCategoriesData.data]?.map((category) => getCategoryTracks(category.id, Hub.Download)) : []),
        [getCategoryTracks, onlineCrateCategoriesData?.data]
    );

    const EmptyComponent = useMemo(
        () => (
            <EmptyState
                title="Looks like you haven't added any tracks to your crate yet."
                subtitle="Start building a crate to download large quantities of tracks with one click. Your added tracks will appear here once you've created them."
                icon="search-outline-icon"
                href="/trending"
                actionLabel="Explore Trending Tracks"
                actionIcon="chevron-right-icon"
                noPadding
            />
        ),
        []
    );

    const cratesList = useMemo(() => {
        let playlistArray = [];
        switch (sortType) {
            case 'most_recent':
                playlistArray = onlineCategoryList.slice().sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
                break;
            case 'title_asc':
                playlistArray = onlineCategoryList.slice().sort((a, b) => (a.name > b.name ? 1 : -1));
                break;
            case 'title_desc':
                playlistArray = onlineCategoryList.slice().sort((a, b) => (a.name < b.name ? 1 : -1));
                break;
            default:
                playlistArray = onlineCategoryList.slice();
                break;
        }

        return playlistArray;
    }, [onlineCategoryList, sortType]);

    return (
        <>
            <Title platform="download" title={title} />
            <div className={classNames(styles['crate-index'], 'spacing__window')}>
                <PageHeader
                    title={onlineCategoryList?.length <= 1 ? 'Your Crate' : `Your Crates (${onlineCategoryList?.length})`}
                    searchPlaceholder="Search Crates"
                    onSort={setSortType}
                    selectedSortType={sortType}
                />
                {!DOWNLOAD_ONLY && (
                    <div className={styles['crate-index__subtitle']}>
                        <SecondaryPageTitle title="Download Crate" counter={onlineCategoryList?.length?.toString()} noPadding />
                    </div>
                )}
                {isEverythingLoaded ? (
                    onlineCategoryList?.length ? (
                        <CrateCategoryList items={cratesList} hub={Hub.Download} />
                    ) : (
                        EmptyComponent
                    )
                ) : (
                    <div className="spacing--top">
                        <BreakpointView
                            desktopChildren={<GhostComponent type="cards" elementsCount={5} cardSize="user-playlist" linesWidthArray={[70, 90]} lineHeight={17} grid noPadding />}
                            mobileChildren={<GhostComponent type="track-list" elementsCount={5} noPadding />}
                        />
                    </div>
                )}
                {!DOWNLOAD_ONLY && (
                    <>
                        <div className={styles['crate-index__subtitle']}>
                            <SecondaryPageTitle title="Offline Crate" counter={offlineCategoryList?.length?.toString()} noPadding />
                        </div>
                        {offlineCategoryList?.length ? <CrateCategoryList items={offlineCategoryList} hub={Hub.Stream} /> : EmptyComponent}
                    </>
                )}
            </div>
        </>
    );
};

export default CrateLandingPage;

export async function getStaticProps() {
    return {
        props: {
            title: 'My Crate',
        },
    };
}
